import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cos");
      setItems(data);
    } catch {
      toast.error("Failed to load CoS list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (id, cert) => {
    try {
      const res = await fetch(`${API}/cos/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cert ? `CoS-${cert}.pdf` : `CoS.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this certificate? This cannot be undone.")) return;
    try {
      await api.delete(`/cos/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/cos/${id}/duplicate`);
      toast.success("Duplicated");
      navigate(`/cos/${data.id}`);
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/library/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const bits = [];
      if (data?.saved?.sponsor) bits.push(`sponsor: ${data.saved.sponsor.sponsor_name || data.saved.sponsor.sponsor_licence_number}`);
      if (data?.saved?.job) bits.push(`job: ${data.saved.job.job_title || data.saved.job.job_type}`);
      toast.success(`Imported ${bits.join(" · ") || "no reusable details found"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const statusClass = (s) => {
    const k = (s || "").toLowerCase();
    if (k.includes("ready")) return "ready";
    if (k.includes("assign")) return "assigned";
    if (k.includes("used")) return "used";
    return "";
  };

  return (
    <AppLayout
      activeMenu="view"
      breadcrumb={[
        { label: "Sponsorship management system", to: "/" },
        { label: "Workers", to: "/cos" },
        { label: "View CoS" },
      ]}
    >
      <h1>CoS search results</h1>
      <p>
        The table below shows CoS that match your search criteria, or the contents of the batch
        you selected. To view in more detail, select the CoS that you wish to view and choose{" "}
        <strong>Edit</strong>. You can also <strong>Import</strong> an existing UK CoS PDF to store
        its sponsor and job details for reuse.
      </p>

      <div className="sms-toolbar">
        <div />
        <div className="sms-toolbar-right">
          <button
            type="button"
            data-testid="import-cos-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="sms-btn secondary"
          >
            {importing ? "Importing…" : "Import CoS PDF"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={onImport}
            className="hidden"
            style={{ display: "none" }}
            data-testid="import-file-input"
          />
          <button
            type="button"
            data-testid="new-cos-btn"
            onClick={() => navigate("/cos/new")}
            className="sms-btn"
          >
            Create new
          </button>
        </div>
      </div>

      <div className="sms-panel">
        <div className="sms-panel-title">Search results</div>
        <div className="sms-panel-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 24, color: "#666" }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 24, color: "#666" }}>
              No certificates yet.{" "}
              <Link to="/cos/new">Create your first certificate</Link>.
            </div>
          ) : (
            <table className="sms-table" data-testid="cos-table">
              <thead>
                <tr>
                  <th>Certificate number</th>
                  <th>Status</th>
                  <th>Family name</th>
                  <th>Given name</th>
                  <th>Date of birth</th>
                  <th>Nationality</th>
                  <th>Route</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={`cos-row-${c.id}`}>
                    <td>
                      <Link to={`/cos/${c.id}/view`} style={{ fontFamily: "monospace" }}>
                        {c.certificate_number || "—"}
                      </Link>
                    </td>
                    <td>
                      <span className={`sms-status-pill ${statusClass(c.current_certificate_status)}`}>
                        {c.current_certificate_status || "—"}
                      </span>
                    </td>
                    <td>{c.family_name || "—"}</td>
                    <td>{c.given_names || "—"}</td>
                    <td>{c.date_of_birth || "—"}</td>
                    <td>{c.nationality || "—"}</td>
                    <td style={{ maxWidth: 180 }}>{c.tier_and_category || "—"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        data-testid={`view-btn-${c.id}`}
                        onClick={() => navigate(`/cos/${c.id}/view`)}
                        className="sms-btn small secondary"
                        title="View"
                      >
                        View
                      </button>{" "}
                      <button
                        type="button"
                        data-testid={`download-btn-${c.id}`}
                        onClick={() => downloadPdf(c.id, c.certificate_number)}
                        className="sms-btn small secondary"
                        title="Download PDF"
                      >
                        PDF
                      </button>{" "}
                      <button
                        type="button"
                        data-testid={`edit-btn-${c.id}`}
                        onClick={() => navigate(`/cos/${c.id}`)}
                        className="sms-btn small secondary"
                        title="Edit"
                      >
                        Edit
                      </button>{" "}
                      <button
                        type="button"
                        data-testid={`duplicate-btn-${c.id}`}
                        onClick={() => duplicate(c.id)}
                        className="sms-btn small secondary"
                        title="Duplicate"
                      >
                        Copy
                      </button>{" "}
                      <button
                        type="button"
                        data-testid={`delete-btn-${c.id}`}
                        onClick={() => del(c.id)}
                        className="sms-btn small secondary"
                        style={{ color: "#8f1414" }}
                        title="Delete"
                      >
                        Del
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
